// =============================================================================
// src/main.ts — ponto de entrada do Mapa Político (Vite + TS vanilla).
//
// INTEGRACAO FINAL — 100% TYPESCRIPT. Monta o chrome + as 8 secoes scrollaveis +
//   as rotas fullscreen (Explorar/Workspace/Grafo) + os takeovers (Crise/
//   Reputacao/Copiloto-chat) nos pontos de montagem do shell (index.html), expoe
//   os comandos globais e inicializa os MODULOS TS (init) — o runtime legado
//   (public/legacy/*.js) foi APOSENTADO e NAO e mais injetado.
//
// Por que TS para o comportamento? As views/chrome/features ports ja entregam a
//   MARCACAO fiel (render()) e o COMPORTAMENTO (init()): MapController,
//   SectionManager, GrafoCanvas, command palette, rotas, takeovers — todos
//   portados de assets/*.js com timings/easings/thresholds identicos. main.ts
//   monta o DOM primeiro e dispara os init() na MESMA SEQUENCIA do bootstrap
//   legado (assets/unified-app.js 3235-3256), agora sem <script> classico.
//
// Coexistencia controlada: a feature Explorar (explore-globals.ts) le alguns
//   singletons via `window` (MapController/TopbarNav/SearchPalette/Grafo/Workspace
//   Route) para coordenacao cross-modulo — exatamente como o explore.js legado
//   lia os globais. Publicamos os singletons TS reais em `window` (publishTsGlobals)
//   para preservar esse contrato sem reintroduzir o runtime legado.
//
// O painel "Contexto atual" + táticas do Copiloto vem do port TS
//   (CopilotSidebarChrome.init) — o bootstrap legado OMITIA CopilotTactics.init()
//   por isso; aqui a sidebar TS ja cobre as táticas (sem binding duplo).
//
// Ordem de import de CSS: IDENTICA ao <head> de unified.html (shared -> unified ->
//   crisis -> reputation -> copilot-chat -> explore -> inline-overrides).
// =============================================================================

import './styles/shared.css';
import './styles/unified.css';
import './styles/crisis.css';
import './styles/reputation.css';
import './styles/copilot-chat.css';
import './styles/explore.css';
import './styles/explore-vote.css';
import './styles/explore-g26.css';
import './styles/analytics.css';
import './styles/analytics-modules.css';
import './styles/analytics-detail.css';
import './styles/analytics-workflows.css';
import './styles/analytics-reference-flow.css';
import './styles/analytics-digest-report.css';
import './styles/analytics-insights.css';
import './styles/analytics-motion.css';
// briefing.css depois dos CSS herdados (reforma 2026-06: rota nova, sem
// dependencia de cascata legada) e antes do inline-overrides (que fecha a pilha).
import './styles/briefing.css';
// login.css (gate de sessao 2026-07): tela pre-boot isolada (.auth-login-*),
// sem dependencia de cascata — entra junto das rotas novas.
import './styles/login.css';
import './styles/inline-overrides.css';
// observatory.css POR ULTIMO (reforma 2026-06, Fase 3): componentes novos
// compartilhados ganham empates de cascata sem !important.
import './styles/observatory.css';

import {
  createAppContext,
  withAtalaiaClient,
  withMapController,
  withSectionInsights,
  withSentinelClient,
} from './app/context';
import type { AppContext, AppModule } from './app/types';
import { setOwnerIdentity, composeOwnerIdentity } from './app/owner-identity';
import { createAtalaiaClient } from './data/atalaia-client';
import { createSentinelClient } from './data/sentinel-client';
import { fetchSectionInsights } from './data/insights-client';
import { fetchOwnerIdentity } from './data/identity-client';
import { debugLiveIntegration } from './data/live-debug';
import { resolveExploreLiveConfig } from './data/explore-live-config';
import { createAnalyticsLiveClient } from './data/analytics-client';
import { resolveAnalyticsLiveConfig } from './data/analytics-live-config';
import { resolveSentinelLiveConfig } from './data/sentinel-live-config';
import { SECTION_INSIGHTS } from './data/section-insights';
import { hydrateAnalyticsLive } from './features/analytics-live-hydrate';
import { hydrateAnalyticsInsightEditions } from './features/analytics-insights-hydrate';
import { setAnalyticsActorFeedLoader } from './features/analytics/analytics-actor-feed-store';
import { setAnalyticsFeedPageLoader } from './features/analytics/analytics-feed-page-store';
import {
  fetchAnalyticsFeedPage,
  fetchAnalyticsFeedTotal,
} from './data/analytics-feed-page-client';
import { buildAnalyticsFeedPageItems } from './data/analytics-overlay-feed';
import { analyticsPeriodWindowDays } from './data/analytics-overlay-scope';
import { getAnalyticsViewState } from './features/analytics/analytics-view-state';
import { setAnalyticsInsightEditionBodyLoader } from './features/analytics/analytics-insights-edition-loader';
import { setAnalyticsAuthorBlockSubmitter } from './features/analytics/analytics-block-store';
import { setAnalyticsScopeOverrideSubmitter } from './features/analytics/analytics-scope-store';
import { buildAnalyticsActorFeedItems } from './data/analytics-overlay-feed';
import { hydrateSentinelLive } from './features/sentinel-live-hydrate';

// Core (porte de assets/unified-app.js): mapa + scroll-spy.
import { MapController } from './core/map-controller';
import { SectionManager } from './core/section-manager';
import type { SectionLifecycleModule } from './core/section-manager';

// Chrome (markup fiel + comportamento TS).
import { buildTopbarMarkup, TopbarNav } from './chrome/topbar/index';
import { SearchPalette } from './chrome/search-palette/index';
import { CopilotSidebarChrome } from './chrome/copilot-sidebar/index';
import {
  buildRegionPopupMarkup,
  buildChromeOverlaysMarkup,
} from './chrome/static-shell/index';

// Views scrollaveis (#contentCol) — as 8 secoes do feed, na ordem de unified.html.
import { mountSectionInsightCards } from './views/shared/section-insight-card';
import { mountAiBadges } from './views/shared/ai-badge';
import MapaEleitoralView from './views/mapa-eleitoral/index';
import { perfilRegiaoView } from './views/perfil-regiao/index';
import { comparadorView } from './views/comparador/index';
import { demograficaView } from './views/demografica/index';
import { camadasView } from './views/camadas/index';
import { negociosView } from './views/negocios/index';
import { impostometroView } from './views/impostometro/index';
import { monitoramentoView } from './views/monitoramento/index';
import { cinemaView } from './views/cinema/index';

// Rotas fullscreen (#routeLayer) — markup + comportamento TS.
import {
  buildExplorePageMarkup,
  init as initExplore,
} from './features/explore/index';
import {
  buildBriefingPageMarkup,
  initBriefing,
} from './features/briefing/index';
import {
  buildMapaIaPageMarkup,
  initMapaIa,
} from './features/mapa-ia/index';
import {
  AnalyticsRoute,
  buildAnalyticsPageMarkup,
  initAnalytics,
} from './features/analytics/index';
import { resolveLandingView } from './app/landing-view';
import {
  buildWorkspacePageMarkup,
  workspaceView,
  WorkspaceRoute,
} from './views/workspace/index';
// Grafo de Influencias DESATIVADO (pedido 2026-07-20): `src/views/grafo/**` e seus
// testes seguem em disco, mas o modulo NAO e montado, inicializado nem publicado no
// window — o `syncFromURL()` da grafo-route reabriria a rota por `?view=grafo` mesmo
// com os pills desligados, entao a desativacao precisa cortar o bootstrap, nao so a UI.
// Para religar: restaurar os imports + buildGrafoPageMarkup()/grafoView.init()/win.GrafoRoute.

// Takeovers (#routeLayer) — markup fiel + comportamento TS (aposenta os IIFE legados).
import {
  buildCrisisTakeoverMarkup,
  initCrisisTakeover,
} from './features/crisis/index';
import {
  buildReputationTakeoverMarkup,
  initReputationTakeover,
} from './features/reputation/index';
// initCopilotChat saiu do import junto com sua chamada (Copiloto desativado,
// 2026-07-21). O markup do takeover CONTINUA sendo montado, e `openCopilotChat`
// segue publicado no window — porem como no-op, por nunca receber a impl viva.
import {
  buildCopilotChatTakeoverMarkup,
  openCopilotChat,
} from './features/copilot-chat/index';

// Gate de login (2026-07): sessao mock persistida + tela porte do shadcn login-03.
import { hasAuthSession, presentLoginTakeover } from './features/auth/index';

/**
 * As 9 secoes scrollaveis/by-scroll-spy registradas no SectionManager, na ordem do
 * feed de unified.html (mapa -> perfil -> comparador -> demografica -> camadas ->
 * negocios -> impostometro -> monitoramento) + cinema (sem markup estatico, gerado
 * em runtime). O SectionManager le `.u-section` do DOM e indexa estes modulos por id.
 */
const CONTENT_SECTIONS: readonly AppModule[] = [
  MapaEleitoralView,
  perfilRegiaoView,
  comparadorView,
  demograficaView,
  camadasView,
  negociosView,
  impostometroView,
  monitoramentoView,
];

/**
 * Lista completa de modulos com ciclo de vida (activate/deactivate) para o
 * SectionManager — as 8 secoes do feed + o Modo Cinema. Cinema entra aqui (e nao em
 * CONTENT_SECTIONS) porque seu render() e vazio: o overlay e gerado em runtime na
 * ativacao, mas o scroll-spy/TopbarNav precisam ativar/desativar a secao por id.
 */
const SCROLL_SPY_VIEWS: readonly SectionLifecycleModule[] = [
  ...(CONTENT_SECTIONS as readonly SectionLifecycleModule[]),
  cinemaView,
];

/** Publica os AppCommands no window para os `onclick` inline herdados do mockup. */
function exposeCommandsOnWindow(context: AppContext): void {
  window.sendPrompt = context.commands.sendPrompt;
  window.openLink = context.commands.openLink;
  // Late-binding: openCopilotChat (copilot-chat/index) delega para a impl real
  // publicada por initCopilotChat; antes do init faz no-op seguro.
  window.openCopilotChat = openCopilotChat;
}

/**
 * Singletons TS lidos via `window` pela feature Explorar (explore-globals.ts),
 * espelhando os globais que o explore.js legado consumia. Publicados antes de
 * initExplore() para que a primeira abertura ja os encontre.
 */
interface TsCoexistenceWindow {
  MapController?: typeof MapController;
  SectionManager?: typeof SectionManager;
  TopbarNav?: typeof TopbarNav;
  SearchPalette?: typeof SearchPalette;
  WorkspaceRoute?: typeof WorkspaceRoute;
  AnalyticsRoute?: typeof AnalyticsRoute;
}

/**
 * Publica os singletons TS no `window` para a coordenacao cross-modulo da Explorar
 * (getMapController/getTopbarNav/getSearchPalette/getGrafoRoute/getWorkspaceRoute).
 * Substitui os globais do runtime legado por suas contrapartes TS reais.
 */
function publishTsGlobals(): void {
  const win = window as unknown as TsCoexistenceWindow;
  win.MapController = MapController;
  win.SectionManager = SectionManager;
  win.TopbarNav = TopbarNav;
  win.SearchPalette = SearchPalette;
  win.WorkspaceRoute = WorkspaceRoute;
  win.AnalyticsRoute = AnalyticsRoute;
}

/** Devolve o elemento de montagem por id ou lanca com o id ofensivo (boundary check). */
function mountPoint(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`main.ts: ponto de montagem ausente no shell, recebido id=${JSON.stringify(id)}, esperado <div id> em index.html`);
  }
  return el;
}

/**
 * Monta a sidebar do Copiloto: o render() devolve <aside id="aiCopilot"> + o botao
 * .copilot-reopen. Movemos o aside para substituir o placeholder #aiCopilot e o
 * botao para #copilotReopenMount (preserva ids/markup; evita aside aninhado).
 */
function mountCopilotSidebar(): void {
  const staging = document.createElement('div');
  staging.innerHTML = CopilotSidebarChrome.render();
  const renderedAside = staging.querySelector('aside#aiCopilot');
  const reopenBtn = staging.querySelector('button.copilot-reopen');
  if (renderedAside) mountPoint('aiCopilot').replaceWith(renderedAside);
  if (reopenBtn) mountPoint('copilotReopenMount').replaceWith(reopenBtn);
}

/** Concatena render() das 8 secoes em #contentCol, na ordem do feed. */
function mountContentSections(): void {
  mountPoint('contentCol').innerHTML = CONTENT_SECTIONS.map((view) => view.render()).join('\n');
}

/**
 * Monta as rotas fullscreen + takeovers + overlays de chrome em #routeLayer.
 * Ordem segue unified.html (explore -> workspace -> grafo -> palette/dock ->
 * takeovers crise/reputacao/copilot-chat), preservando a cascata de z-index.
 */
function mountRouteLayer(): void {
  mountPoint('routeLayer').innerHTML = [
    buildBriefingPageMarkup(),
    buildExplorePageMarkup(),
    buildWorkspacePageMarkup(),
    buildChromeOverlaysMarkup(),
    buildCrisisTakeoverMarkup(),
    buildReputationTakeoverMarkup(),
    buildCopilotChatTakeoverMarkup(),
    buildMapaIaPageMarkup(),
    buildAnalyticsPageMarkup(),
  ].join('\n');
}

/** Monta a topbar e o region popup nos seus pontos de montagem. */
function mountChrome(): void {
  mountPoint('appTopbar').innerHTML = buildTopbarMarkup();
  mountPoint('regionPopupMount').innerHTML = buildRegionPopupMarkup();
  mountCopilotSidebar();
}

/** Switches globais (.u-switch) — porte de unified-app.js 3245-3247. */
function bindGlobalSwitches(): void {
  document.querySelectorAll('.u-switch').forEach((sw) => {
    sw.addEventListener('click', () => sw.classList.toggle('on'));
  });
}

/** CTA do region popup (#u-pop-cta) — porte de unified-app.js 3250-3254. */
function bindRegionPopupCta(): void {
  const popBtn = document.getElementById('u-pop-cta');
  if (popBtn) {
    popBtn.addEventListener('click', () => window.sendPrompt('Abra o perfil completo desta região'));
  }
}

/**
 * Inicializa os modulos TS na ORDEM do bootstrap legado (assets/unified-app.js
 * 3235-3256), agora sem <script> legados. Cada view recebe o AppContext (com o
 * MapController em ctx.map) via init(ctx); rotas/chrome ligam seus listeners; as
 * features (crise/reputacao/copilot-chat/explore) substituem os IIFE auto-bootados.
 *
 * Diferenca consciente do legado: CopilotTactics.init() e omitido (a sidebar TS ja
 * cobre as táticas). A ordem dos demais init() e fiel: MapController -> SectionManager
 * -> Workspace -> Grafo -> Topbar -> SearchPalette -> Copiloto sidebar/chat -> crise/
 * reputacao -> explore. Por fim os switches globais e o CTA do popup.
 */
function initTsRuntime(context: AppContext): void {
  MapController.init();
  SectionManager.init(SCROLL_SPY_VIEWS, context);
  initContentSections(context);
  // Surfacing dos insights ao vivo (gold.section_insight via MiniMax M3) no topo de
  // cada seção do feed — antes só a sidebar do Copiloto consumia ctx.sectionInsights.
  mountSectionInsightCards(context);
  workspaceView.init(context);
  TopbarNav.init(context, SectionManager);
  SearchPalette.init(context);
  // CopilotSidebarChrome.init(context) OMITIDO — Copiloto IA desativado (2026-07-21).
  // A sidebar continua MONTADA e visivel (mountCopilotSidebar, acima), mas sem init
  // nao ha Ctrl+J, nem "Nova pergunta", nem taticas. Desligar aqui e o que fecha o
  // atalho de teclado: ele e ligado dentro deste init, nao no markup.
  // Efeito colateral aceito: o painel "Contexto atual" exibe o conteudo ESTATICO do
  // markup em vez dos insights ao vivo (quem os injeta e este init).
  initFeatures(context);
  bindGlobalSwitches();
  bindRegionPopupCta();
  // Badges "IA" nos cabeçalhos das áreas-IA (sidebar/chat/crise/reputação/monitoramento),
  // após takeovers (mountRouteLayer) + sidebar + feed estarem no DOM. Idempotente.
  mountAiBadges();
}

/**
 * Chama init(ctx) de cada secao do feed apos a marcacao estar montada e o
 * SectionManager indexado. Replica o passo do legado em que Sections.* ligava seus
 * listeners; aqui o ctx ja carrega o MapController (ctx.map) para as views de mapa.
 */
function initContentSections(context: AppContext): void {
  CONTENT_SECTIONS.forEach((view) => view.init(context));
}

/**
 * Liga as 5 features (takeovers + Explorar + Briefing) — substitui os IIFE
 * auto-bootados de assets/{crisis,reputation,copilot-chat,explore}.js. Ordem fiel
 * ao <head> de unified.html (crisis -> reputation -> copilot-chat -> explore),
 * com o Briefing por ultimo. Reforma 2026-06 (D2): a landing default e o
 * BRIEFING; resolveLandingView preserva os deep-links ?view= (grafo/workspace
 * se auto-abrem via syncFromURL proprio).
 */
function initFeatures(context: AppContext): void {
  // Crise e Reputacao seguem inicializadas de proposito: seus takeovers so abrem pelos
  // launchers da doca, que agora nascem `disabled` — o init apenas liga listeners que
  // nunca disparam, e mante-lo preserva o comportamento intacto para religar.
  initCrisisTakeover(context);
  initReputationTakeover(context);
  // initCopilotChat(context) OMITIDO — Copiloto IA desativado (2026-07-21). Sem ele
  // `liveOpenCopilotChat` fica null e o `openCopilotChat` exportado vira no-op seguro
  // (ja projetado assim em features/copilot-chat/index.ts), fechando TODOS os
  // chamadores de window.openCopilotChat de uma vez.
  const landing = resolveLandingView(window.location.search);
  initExplore(landing === 'explore', context.atalaia);
  initBriefing(context, landing === 'briefing');
  initMapaIa(context);
  initAnalytics(context);
}

/**
 * Cria o AppContext de DI: sempre com o MapController (ctx.map) e, quando
 * VITE_HASURA_URL esta definida (resolveExploreLiveConfig), com o cliente AtalaIA
 * anonimo (ctx.atalaia) para a Explorar hidratar os campos reais ao vivo. Sem a env,
 * ctx.atalaia fica null e a tela cai no dataset estatico (deploy sem backend).
 */
function buildAppContext(): AppContext {
  const base = withMapController(createAppContext(), MapController);
  let ctx = base;
  const exploreLive = resolveExploreLiveConfig(import.meta.env);
  if (exploreLive.enabled) {
    ctx = withAtalaiaClient(ctx, createAtalaiaClient({ endpoint: exploreLive.endpoint }));
  }
  const sentinelLive = resolveSentinelLiveConfig(import.meta.env);
  if (sentinelLive.enabled) {
    ctx = withSentinelClient(
      ctx,
      createSentinelClient({
        endpoint: sentinelLive.graphqlEndpoint,
        clientId: sentinelLive.clientId,
        apiUrl: sentinelLive.apiEndpoint,
      }),
    );
  }
  return ctx;
}

async function hydrateSectionInsights(context: AppContext): Promise<AppContext> {
  const sentinelLive = resolveSentinelLiveConfig(import.meta.env);
  if (!sentinelLive.enabled) {
    debugLiveIntegration('section-insights', 'fallback_static', { reason: 'sentinel_disabled' });
    return context;
  }
  const live = await fetchSectionInsights(sentinelLive.graphqlEndpoint, sentinelLive.clientId);
  if (!live) {
    debugLiveIntegration('section-insights', 'fallback_static', { reason: 'empty_or_failed' });
    return context;
  }
  debugLiveIntegration('section-insights', 'live_gold_section_insight', {
    surfaces: Object.keys(live).length,
  });
  return withSectionInsights(context, { ...SECTION_INSIGHTS, ...live });
}

/**
 * Hidrata a identidade do gabinete (dono) a partir do backend (app_client/actor) e a
 * fixa no resolver (src/app/owner-identity) ANTES de montar o chrome/takeovers — assim
 * os render() já exibem o tenant correto (Omar Aziz no piloto). Falha/sem-env mantém o
 * fallback estático (também Omar). Roda antes de mountChrome/mountRouteLayer.
 */
async function hydrateOwnerIdentity(): Promise<void> {
  const sentinelLive = resolveSentinelLiveConfig(import.meta.env);
  if (!sentinelLive.enabled) {
    debugLiveIntegration('owner-identity', 'fallback_static', { reason: 'sentinel_disabled' });
    return;
  }
  const live = await fetchOwnerIdentity(sentinelLive.graphqlEndpoint, sentinelLive.clientId);
  if (!live) {
    debugLiveIntegration('owner-identity', 'fallback_static', { reason: 'empty_or_failed' });
    return;
  }
  setOwnerIdentity(composeOwnerIdentity(live));
  debugLiveIntegration('owner-identity', 'live_app_client', { name: live.name, party: live.party });
}

/** Analytics live (social_listening): client só nasce com VITE_HASURA_URL no env;
 * sem ele a hidratação resolve estático imediato (mock integral do Analytics). */
function hydrateAnalyticsLiveFromEnv(): Promise<unknown> {
  const analyticsLive = resolveAnalyticsLiveConfig(import.meta.env);
  const analyticsClient = analyticsLive.enabled
    ? createAnalyticsLiveClient({ endpoint: analyticsLive.graphqlEndpoint, clientId: analyticsLive.clientId })
    : null;
  // Curadoria: o botao "Bloquear autor" do feed grava na blocklist via Hasura.
  if (analyticsClient) {
    setAnalyticsAuthorBlockSubmitter((blockRequest) => analyticsClient.blockAuthor(blockRequest));
    // Curadoria por CONTEUDO: "Fora de escopo" grava o override que o scoring respeita.
    setAnalyticsScopeOverrideSubmitter((overrideRequest) => analyticsClient.overrideScope(overrideRequest));
    // Drill-down: chip de personagem busca o conteudo REAL do ator no servidor.
    setAnalyticsActorFeedLoader(async (feedLoad) =>
      buildAnalyticsActorFeedItems(await analyticsClient.fetchActorFeed(feedLoad)));
    // 2026-09-20: a lista de Mencoes vira uma JANELA paginada sobre o conjunto do
    // recorte (21.195 itens no escopo padrao). A contagem so e refeita quando o
    // recorte muda — ela custa um scan no servidor e nao muda entre paginas.
    setAnalyticsFeedPageLoader(async (pageLoad) => {
      const viewState = getAnalyticsViewState();
      const request = {
        scope: viewState.scope,
        showReactions: viewState.showReactions,
        sort: viewState.sort,
        page: pageLoad.page,
        filters: {
          rawPlatforms: pageLoad.rawPlatforms,
          actorSlugs: pageLoad.actorSlugs,
          relations: pageLoad.relations,
          relationPairs: pageLoad.relationPairs,
        },
        withSentiment: true,
        sinceTs: new Date(Date.now() - analyticsPeriodWindowDays(viewState.period) * 86400000).toISOString(),
      };
      const deps = {
        endpoint: analyticsLive.graphqlEndpoint,
        clientId: analyticsLive.clientId,
        fetchImpl: window.fetch.bind(window),
        // Timeout curto: virar pagina e interacao — 8 s travados parecem app quebrado.
        timeoutMs: 4000,
      };
      const [page, totalItems] = await Promise.all([
        fetchAnalyticsFeedPage(deps, request),
        pageLoad.refreshTotal ? fetchAnalyticsFeedTotal(deps, request) : Promise.resolve(null),
      ]);
      return { mentions: buildAnalyticsFeedPageItems(page.rows), hasNextPage: page.hasNextPage, totalItems };
    });
    // Resumo de IA (022): corpo das edicoes dos Cards Taticos sob demanda
    // (prev/next); doc_kind da LINHA e o autoritativo para o parse.
    setAnalyticsInsightEditionBodyLoader(async (editionRef) => {
      const bodyRow = await analyticsClient.fetchInsightEditionBody(editionRef.editionDate);
      if (!bodyRow) return null;
      return {
        markdownBody: bodyRow.markdown_body,
        docKind: bodyRow.doc_kind === 'nota_de_deriva' ? 'nota_de_deriva' : 'edicao_completa',
      };
    });
  }
  // Dashboard e edicoes do digest hidratam em paralelo com catch proprio —
  // falha de um NAO contamina o outro (decisao 2026-07-28).
  return Promise.all([
    hydrateAnalyticsLive(analyticsClient),
    hydrateAnalyticsInsightEditions(analyticsClient),
  ]);
}

/**
 * Bootstrap da integracao 100% TS: cria o contexto de DI (MapController + clientes
 * AtalaIA/Sentinel opcionais), hidrata dados ao vivo, monta o DOM e inicializa modulos.
 */
async function bootstrap(): Promise<void> {
  let context = buildAppContext();
  context = await hydrateSectionInsights(context);
  await hydrateOwnerIdentity();
  // Sentinel (monitoring.*) e Analytics (social_listening, sl_*) em paralelo:
  // mesma Hasura, sources distintos — zero latência extra no caminho feliz.
  await Promise.all([hydrateSentinelLive(context.sentinel), hydrateAnalyticsLiveFromEnv()]);
  exposeCommandsOnWindow(context);
  publishTsGlobals();
  mountChrome();
  mountContentSections();
  mountRouteLayer();
  initTsRuntime(context);
}

/**
 * Gate de autenticacao (mock, 2026-07): sem sessao persistida (features/auth), a
 * tela de login (porte do shadcn login-03) cobre o shell e o bootstrap so roda
 * apos autenticar. O "Sair" da topbar (signOutOperator) limpa a sessao e
 * reapresenta a tela SEM re-bootstrapar — o app segue montado por baixo.
 */
function startAppWithAuthGate(): void {
  if (hasAuthSession()) {
    void bootstrap();
    return;
  }
  presentLoginTakeover({ onAuthenticated: () => void bootstrap() });
}

startAppWithAuthGate();
